import { Row, Table } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, DateRange as ReactDateRange } from 'react-date-range';
import {
  isUnaryRangeFilterOp,
  useMinFilterValue,
} from 'src/components/Filters/Filter.types';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSellerAccountContext } from 'src/contexts/SellerAccountContext';
import { PosSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { ColumnFilterButton } from 'src/core/ui/ColumnFilterButton';
import { TooltipPopover } from 'src/core/ui/TooltipPopover';
import { getUserDisplayName, useGetUserInfos } from 'src/hooks/userGetUserInfo';
import { ArrowRight } from 'src/svgs/ArrowRight';
import { CrossIcon } from 'src/svgs/Viagogo';
import { ListingTableColumnId } from 'src/utils/columns/inventory/inventoryColumnUtils.types';
import { PosManagedMarketplace } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import {
  AUTO_PRICING_RESULT_TYPES_OPTIONS_TO_CID,
  COMP_LISTING_MODE_TO_CID,
  NUMBER_RANGE_FILTER_OP_TO_CID,
  OUTLIER_MODE_TO_CID,
  STANDARD_DEVIATIONS_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { getIsInternationalEvent } from 'src/utils/eventWithDataUtils';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';
import {
  AutoPricingConstraintType,
  AutoPricingResultType,
  FilterOperationType,
  Marketplace,
} from 'src/WebApiController';

import { ColumnFilter } from './ColumnFilter';
import * as styles from './FilterComponents.css';
import { getCurrencySymbol } from './filterFunctions';

interface FilterUIProps<TValue = any> {
  value: TValue;
  onChange: (value: TValue) => void;
  onClear: () => void;
  options?: Record<string, string>;
  counts?: Record<string, number>;
  columnId: string;
  table: Table<any>;
}

/**
 * Number range filter UI - min/max inputs with optional suggestions in a popover
 * Supports both range filtering (min-max) and discrete value selection
 */
export const NumberRangeFilterUI = ({
  value = { min: undefined, max: undefined, selectedValues: [] },
  options = {},
  counts = {},
  onChange,
  onClear,
  columnId,
  table,
  isPercent = false,
}: FilterUIProps<{
  min?: number;
  max?: number;
  selectedValues: string[];
}> & {
  isPercent?: boolean;
}) => {
  const isActive =
    value.min != null || value.max != null || value.selectedValues?.length > 0;

  const minText = useContent(ContentId.Min);
  const maxText = useContent(ContentId.Max);

  // Create filterValue object for compatibility with the min/max inputs
  const filterValue = useMemo(
    () => ({
      min: value.min,
      max: value.max,
      operation: FilterOperationType.Between,
    }),
    [value.min, value.max]
  );

  const setFilterValue = useCallback(
    (v: { min?: number; max?: number; operation: FilterOperationType }) => {
      // When range is changed, clear selected values
      onChange({ min: v.min, max: v.max, selectedValues: [] });
    },
    [onChange]
  );

  const hasSuggestions = Object.keys(options).length > 0;

  // Handler for checkbox changes - clears min/max when checkboxes are used
  const handleSuggestionChange = (newSelectedSuggestions: string[]) => {
    onChange({
      min: undefined,
      max: undefined,
      selectedValues: newSelectedSuggestions,
    });
  };

  return (
    <ColumnFilter.Container isActive={isActive} columnId={columnId}>
      <ColumnFilter.RangeFilter
        min={filterValue.min}
        max={filterValue.max}
        onChange={(v) =>
          setFilterValue({
            min: v.min,
            max: v.max,
            operation: FilterOperationType.Between,
          })
        }
        minPlaceholder={minText}
        maxPlaceholder={maxText}
        suffix={isPercent ? <span>%</span> : undefined}
      />

      {hasSuggestions && (
        <ColumnFilter.List
          options={options}
          value={value.selectedValues || []}
          onChange={handleSuggestionChange}
          counts={counts}
        />
      )}
      <ColumnFilter.ClearButton
        onClear={() => {
          onChange({ min: undefined, max: undefined, selectedValues: [] });
          onClear();
        }}
        disabled={!isActive}
        selectedCount={value.selectedValues?.length || 0}
      />
    </ColumnFilter.Container>
  );
};

/**
 * Multi-select filter UI - dropdown with checkboxes
 */
export const MultiSelectFilterUI = ({
  value = [],
  onChange,
  options = {},
  counts = {},
  columnId,
  onClear,
  table,
  uniqueValueVariant,
}: FilterUIProps<string[]> & {
  uniqueValueVariant?:
    | 'seatingSection'
    | 'seatTraits'
    | 'userTags'
    | 'marketplaceBroadcast'
    | 'outlierMode'
    | 'outlierStdDev'
    | 'compListingMode';
}) => {
  const isActive = value.length > 0;

  const [searchText, setSearchText] = useState('');

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchText) return options;

    const lowerSearch = searchText.toLowerCase();
    return Object.fromEntries(
      Object.entries(options).filter(
        ([key, label]) =>
          key.toLowerCase().includes(lowerSearch) ||
          label.toLowerCase().includes(lowerSearch)
      )
    );
  }, [options, searchText]);

  // Always show custom popover with search + checkboxes
  return (
    <ColumnFilter.Container isActive={isActive} columnId={columnId}>
      <div>
        <ColumnFilter.SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search..."
        />
        <ColumnFilter.List
          options={filteredOptions}
          value={value}
          onChange={onChange}
          counts={counts}
        />
      </div>
      <ColumnFilter.ClearButton
        onClear={() => {
          onChange([]);
          onClear();
        }}
        disabled={!isActive}
        selectedCount={value.length}
      />
    </ColumnFilter.Container>
  );
};

/**
 * Text search filter UI - simple text input with debounce
 */
export const TextSearchFilterUI = ({
  value = '',
  onChange,
  onClear,
  columnId,
}: FilterUIProps<string>) => {
  const [localValue, setLocalValue] = useState(value);

  const clearText = useContent(ContentId.ClearFilter);
  const searchText = useContent(ContentId.Search);

  // Debounce logic
  useEffect(() => {
    const handler = setTimeout(() => {
      onChange(localValue);
    }, 300);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValue]);

  const isActive = Boolean(value);

  return (
    <ColumnFilter.Container isActive={isActive} columnId={columnId}>
      <Stack direction="column" gap="m">
        <PosTextField
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          placeholder={searchText}
          onClick={(e) => e.stopPropagation()}
          rootProps={{
            style: {
              borderRadius: '12px',
              fontSize: vars.typography.fontSize.base,
              padding: `${vars.spacing.sm} ${vars.spacing.m}`,
            },
          }}
        />
        <ColumnFilter.ClearButton
          text={clearText}
          onClear={() => {
            setLocalValue('');
            onClear();
          }}
          disabled={!isActive}
          selectedCount={0}
        />
      </Stack>
    </ColumnFilter.Container>
  );
};

/**
 * Date range filter UI - single calendar with range selection or single date selection
 */
export const DateRangeFilterUI = ({
  value = {
    startDate: undefined,
    endDate: undefined,
    operation: FilterOperationType.Between,
  },
  onChange,
  onClear,
  columnId,
  enableOperationSelect = true,
}: FilterUIProps<{
  startDate?: Date;
  endDate?: Date;
  operation?: FilterOperationType;
}> & {
  enableOperationSelect?: boolean;
}) => {
  const clearText = useContent(ContentId.ClearFilter);

  const currentOperation = value.operation ?? FilterOperationType.Between;
  const needsTwoInputs = !isUnaryRangeFilterOp(currentOperation);
  const useStartDateOnly = useMinFilterValue(currentOperation);

  const isActive = value.startDate != null || value.endDate != null;

  // Create stable default dates that don't change on every render
  const defaultDate = useMemo(() => new Date(), []);

  const ranges = useMemo(
    () => [
      {
        startDate: value.startDate || defaultDate,
        endDate: value.endDate || defaultDate,
        key: 'selection',
      },
    ],
    [value.startDate, value.endDate, defaultDate]
  );

  // Format date for display
  const formatDate = useCallback((date?: Date) => {
    if (!date) return '';
    const locale = getLocaleFromLanguageOrCurrent();
    return date.toLocaleDateString(locale.code || 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }, []);

  // Memoize onChange handlers
  const handleOperationChange = useCallback(
    (newOperation: string) => {
      onChange({
        startDate: value.startDate,
        endDate: value.endDate,
        operation: newOperation as FilterOperationType,
      });
    },
    [onChange, value.startDate, value.endDate]
  );

  const handleDateRangeChange = useCallback(
    (ranges: any) => {
      const selection = ranges.selection;
      if (selection) {
        onChange({
          startDate: selection.startDate,
          endDate: selection.endDate,
          operation: currentOperation,
        });
      }
    },
    [onChange, currentOperation]
  );

  const handleSingleDateChange = useCallback(
    (date: Date) => {
      onChange({
        startDate: useStartDateOnly ? date : undefined,
        endDate: useStartDateOnly ? undefined : date,
        operation: currentOperation,
      });
    },
    [onChange, useStartDateOnly, currentOperation]
  );

  const handleClear = useCallback(() => {
    onChange({
      startDate: undefined,
      endDate: undefined,
      operation: currentOperation,
    });
    onClear();
  }, [onChange, onClear, currentOperation]);

  return (
    <ColumnFilter.Container
      isActive={isActive}
      columnId={columnId}
      style={{ width: '350px' }}
    >
      {enableOperationSelect && (
        <PosSelect
          value={currentOperation ?? undefined}
          valueOptionsContent={NUMBER_RANGE_FILTER_OP_TO_CID}
          enableEmptySelection={false}
          onChange={handleOperationChange}
          style={{
            borderRadius: 12,
            fontSize: vars.typography.fontSize.base,
            padding: `${vars.spacing.sm} ${vars.spacing.m}`,
          }}
        />
      )}

      {/* Date display text */}
      {needsTwoInputs ? (
        <Stack
          direction="row"
          gap="m"
          justifyContent="center"
          className={`${styles.dateDisplayContainer} ${
            value.startDate || value.endDate
              ? styles.dateDisplayText
              : styles.dateDisplayPlaceholder
          }`}
        >
          <span>{formatDate(value.startDate) || 'Start Date'}</span>
          <span>-</span>
          <span>{formatDate(value.endDate) || 'End Date'}</span>
        </Stack>
      ) : (
        <div
          className={`${styles.dateDisplayContainer} ${
            (useStartDateOnly ? value.startDate : value.endDate)
              ? styles.dateDisplayText
              : styles.dateDisplayPlaceholder
          }`}
        >
          {formatDate(useStartDateOnly ? value.startDate : value.endDate) ||
            'Select Date'}
        </div>
      )}

      <div className={styles.customDateRangeContainer}>
        {needsTwoInputs ? (
          <ReactDateRange
            className={styles.dateRangePicker}
            ranges={ranges}
            onChange={handleDateRangeChange}
            locale={getLocaleFromLanguageOrCurrent()}
            showDateDisplay={false}
            direction="vertical"
            months={1}
            rangeColors={[vars.color.backgroundHighlight]}
          />
        ) : (
          <Calendar
            className={styles.dateRangePicker}
            date={
              (useStartDateOnly ? value.startDate : value.endDate) ||
              defaultDate
            }
            onChange={handleSingleDateChange}
            locale={getLocaleFromLanguageOrCurrent()}
            color={vars.color.backgroundHighlight}
          />
        )}
      </div>

      <ColumnFilter.ClearButton
        text={clearText}
        onClear={handleClear}
        disabled={!isActive}
        selectedCount={0}
      />
    </ColumnFilter.Container>
  );
};

/**
 * Boolean filter UI - 3-way toggle (All/Yes/No)
 */
export const BooleanFilterUI = ({
  value,
  onChange,
}: FilterUIProps<boolean | null>) => {
  const allText = useContent(ContentId.All);
  const yesText = useContent(ContentId.Yes);
  const noText = useContent(ContentId.No);
  const clearText = useContent(ContentId.ClearFilter);

  // Convert boolean to string for PosSelect
  const currentValue =
    value === null || value === undefined ? 'all' : String(value);

  const isActive = Boolean(value);

  const valueOptionsContent = {
    all: allText,
    true: yesText,
    false: noText,
  };

  return (
    <ColumnFilter.Container isActive={isActive} style={{ width: '150px' }}>
      <PosSelect
        value={currentValue}
        onChange={(newValue) => {
          if (newValue === 'all') {
            onChange(null);
          } else {
            onChange(newValue === 'true');
          }
        }}
        valueOptionsContent={valueOptionsContent}
        enableEmptySelection={false}
        style={{
          borderRadius: '12px',
          fontSize: vars.typography.fontSize.base,
          padding: `${vars.spacing.sm} ${vars.spacing.m}`,
        }}
      />
      <ColumnFilter.ClearButton
        text={clearText}
        onClear={() => onChange(null)}
        disabled={!currentValue}
        selectedCount={0}
      />
    </ColumnFilter.Container>
  );
};

/**
 * User tag filter UI - resolves user IDs to display names using context + API
 */
export const UserTagFilterUI = ({
  value = [],
  onChange,
  onClear,
  table,
  columnId,
}: FilterUIProps<string[]>) => {
  const { allActiveUserInfos, isLoading: contextLoading } =
    useSellerAccountContext();
  const data = table.getCoreRowModel().flatRows;

  const [searchText, setSearchText] = useState('');

  // Get user IDs from the options prop (already extracted by extractor)
  const { allUserIds, counts } = useMemo(() => {
    const { options, counts } = extractUniqueValues(data, columnId, 'userTags');
    return { allUserIds: Object.keys(options), counts };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, columnId]);

  // Get user IDs that are NOT in context (need to fetch from API)
  const missingUserIds = useMemo(() => {
    return allUserIds.filter((userId) => !allActiveUserInfos?.[userId]);
  }, [allUserIds, allActiveUserInfos]);

  const getUserInfosQuery = useGetUserInfos(
    missingUserIds.length > 0 ? missingUserIds : []
  );

  // Build display options: map user IDs to display names
  const displayOptions = useMemo(() => {
    // Wait for context to load
    if (contextLoading || !allActiveUserInfos) {
      return {};
    }

    const resolved: Record<string, string> = {};
    allUserIds.forEach((userId) => {
      // Try context first (pre-loaded)
      let displayName = allActiveUserInfos?.[userId]?.displayName;

      // Fall back to fetched data for missing users
      if (!displayName && getUserInfosQuery.data?.[userId]) {
        displayName = getUserDisplayName(getUserInfosQuery.data[userId]);
      }

      // Use display name or fall back to user ID
      resolved[userId] = displayName || userId;
    });
    return resolved;
  }, [allUserIds, allActiveUserInfos, getUserInfosQuery.data, contextLoading]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchText) return displayOptions;

    const lowerSearch = searchText.toLowerCase();
    return Object.fromEntries(
      Object.entries(displayOptions).filter(
        ([key, label]) =>
          key.toLowerCase().includes(lowerSearch) ||
          label.toLowerCase().includes(lowerSearch)
      )
    );
  }, [displayOptions, searchText]);

  const isActive = value.length > 0;
  const isLoading =
    (contextLoading || getUserInfosQuery.isLoading) &&
    missingUserIds.length > 0;

  if (isLoading) {
    return (
      <TooltipPopover
        triggerContent={<ColumnFilterButton isActive={isActive} />}
      >
        <div>Loading users...</div>
      </TooltipPopover>
    );
  }

  return (
    <ColumnFilter.Container isActive={isActive} columnId={columnId}>
      <div>
        <ColumnFilter.SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search users..."
        />
        <ColumnFilter.List
          options={filteredOptions}
          value={value}
          onChange={onChange}
          counts={counts}
        />
      </div>
      <ColumnFilter.ClearButton
        onClear={() => {
          onChange([]);
          onClear();
        }}
        disabled={!isActive}
        selectedCount={value.length}
      />
    </ColumnFilter.Container>
  );
};

/**
 * Dual mode number filter UI - toggle between currency ($) and percentage (%) modes
 */
export const DualModeNumberFilterUI = ({
  value = { mode: undefined, min: undefined, max: undefined },
  onChange,
  onClear,
  table,
  columnId,
}: FilterUIProps<{
  mode?: 'absolute' | 'relative';
  min?: number;
  max?: number;
}>) => {
  const clearText = useContent(ContentId.ClearFilter);

  // Default to absolute mode
  const selectedMode = value.mode || 'absolute';
  const prefix = selectedMode === 'absolute' ? '$' : '%';

  const isActive = value.min != null || value.max != null;

  return (
    <ColumnFilter.Container isActive={isActive} columnId={columnId}>
      <Stack direction="column" gap="m">
        {/* Mode selector */}
        <PosSelect
          value={selectedMode}
          onChange={(mode) =>
            onChange({
              mode: mode as 'absolute' | 'relative',
              min: value.min,
              max: value.max,
            })
          }
          valueOptionsContent={{
            absolute: 'Currency ($)',
            relative: 'Percentage (%)',
          }}
          enableEmptySelection={false}
          style={{
            borderRadius: vars.borderRadius.base,
            fontSize: vars.typography.fontSize.base,
            padding: `${vars.spacing.sm} ${vars.spacing.m}`,
          }}
        />

        {/* Min/Max inputs side-by-side */}
        <Stack direction="row" gap="m">
          <PosTextField
            type="number"
            value={value.min ?? ''}
            onChange={(e) => {
              const v = e.target.value ? Number(e.target.value) : undefined;
              onChange({ mode: selectedMode, min: v, max: value.max });
            }}
            placeholder="Min"
            prefixDisplay={<span>{prefix}</span>}
            rootProps={{
              style: {
                borderRadius: vars.borderRadius.base,
                fontSize: vars.typography.fontSize.base,
                padding: `${vars.spacing.sm} ${vars.spacing.m}`,
              },
            }}
          />
          <ArrowRight size={'40px'} />
          <PosTextField
            type="number"
            value={value.max ?? ''}
            onChange={(e) => {
              const v = e.target.value ? Number(e.target.value) : undefined;
              onChange({ mode: selectedMode, min: value.min, max: v });
            }}
            placeholder="Max"
            prefixDisplay={<span>{prefix}</span>}
            rootProps={{
              style: {
                borderRadius: vars.borderRadius.base,
                fontSize: vars.typography.fontSize.base,
                padding: `${vars.spacing.sm} ${vars.spacing.m}`,
              },
            }}
          />
        </Stack>
        <ColumnFilter.ClearButton
          text={clearText}
          onClear={() => {
            onChange({ mode: selectedMode, min: undefined, max: undefined });
            onClear();
          }}
          disabled={!isActive}
          selectedCount={0}
        />
      </Stack>
    </ColumnFilter.Container>
  );
};

/**
 * Money range filter UI - currency selector + amount inputs with currency symbol
 */
export const MoneyRangeFilterUI = ({
  value = {
    currency: undefined,
    min: undefined,
    max: undefined,
    operation: FilterOperationType.Between,
    autoPricingResultType: undefined,
  },
  onChange,
  onClear,
  table,
  columnId,
  enableOperationSelect = true,
}: FilterUIProps<{
  currency?: string;
  min?: number;
  max?: number;
  operation?: FilterOperationType;
  autoPricingResultType?: AutoPricingResultType;
}> & {
  enableOperationSelect?: boolean;
}) => {
  const clearFilterText = useContent(ContentId.ClearFilter);
  const minText = useContent(ContentId.Min);
  const maxText = useContent(ContentId.Max);

  // Default operation to Between if not provided
  const currentOperation = value.operation ?? FilterOperationType.Between;

  // Determine if we need two inputs or one based on operation
  const needsTwoInputs = !isUnaryRangeFilterOp(currentOperation);
  const useMinValueOnly = useMinFilterValue(currentOperation);

  // Extract unique currencies from table
  const availableCurrencies = useMemo(() => {
    const currencies = new Set<string>();
    table.getCoreRowModel().flatRows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const moneyValue = row.getValue(columnId);

        let currency: string | null = null;

        // Extract currency from UiMoney object
        if (
          moneyValue &&
          typeof moneyValue === 'object' &&
          'currency' in moneyValue
        ) {
          currency = (moneyValue as any).currency;
        } else if (typeof moneyValue === 'number') {
          // Plain number - get currency from row.original
          const listing = (row.original as any)?.listing;
          const sale = (row.original as any)?.sale;
          const purchase = row.original as any;
          currency = listing?.currency || sale?.currency || purchase?.currency;
        }

        if (currency) {
          currencies.add(currency);
        }
      }
    });
    return Array.from(currencies);
  }, [table, columnId]);

  // Auto-initialize currency in filter state when currencies are detected
  useEffect(() => {
    if (!value.currency && availableCurrencies.length > 0) {
      let defaultCurrency = availableCurrencies[0];
      if (availableCurrencies.includes('USD')) {
        defaultCurrency = 'USD';
      }
      onChange({
        currency: defaultCurrency,
        min: value.min,
        max: value.max,
        operation: currentOperation,
      });
    }
  }, [
    availableCurrencies,
    value.currency,
    value.min,
    value.max,
    currentOperation,
    onChange,
  ]);

  // Extract rows once for use in memoization
  const flatRows = table.getCoreRowModel().flatRows;

  // Extract AutoPricingResultTypes for AllInPrice column
  const autoPricingResultTypeOptions = useMemo(() => {
    if (columnId !== ListingTableColumnId.AllInPrice) {
      return {};
    }

    const options: Record<string, string> = {};

    flatRows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const listing = (row.original as any)?.listing;

        // Skip if listing not loaded yet
        if (!listing) {
          return;
        }

        const isAutoPrc = listing.isAutoPrc;
        const autoPrcResult = listing.autoPrcResult;
        const autoPrcConstraintType = listing.autoPrcConstraintType;

        if (
          isAutoPrc &&
          autoPrcConstraintType !== AutoPricingConstraintType.None &&
          autoPrcResult &&
          autoPrcResult !== AutoPricingResultType.None
        ) {
          if (!options[autoPrcResult]) {
            const displayName =
              AUTO_PRICING_RESULT_TYPES_OPTIONS_TO_CID[
                autoPrcResult as AutoPricingResultType
              ];
            if (displayName) {
              options[autoPrcResult] = displayName;
            }
          }
        }
      }
    });
    return options;
  }, [flatRows, columnId]);

  const selectedCurrency = value.currency || 'USD';
  const currencySymbol = getCurrencySymbol(selectedCurrency);

  const isActive =
    value.min != null || value.max != null || !!value.autoPricingResultType;

  const hasAutoPricingOptions =
    Object.keys(autoPricingResultTypeOptions).length > 0;

  // Memoize onChange handlers
  const handleOperationChange = useCallback(
    (newOperation: string) => {
      onChange({
        currency: selectedCurrency,
        min: value.min,
        max: value.max,
        operation: newOperation as FilterOperationType,
        autoPricingResultType: value.autoPricingResultType,
      });
    },
    [
      onChange,
      selectedCurrency,
      value.min,
      value.max,
      value.autoPricingResultType,
    ]
  );

  const handleCurrencyChange = useCallback(
    (ccy: string) => {
      onChange({
        currency: ccy,
        min: value.min,
        max: value.max,
        operation: currentOperation,
        autoPricingResultType: value.autoPricingResultType,
      });
    },
    [
      onChange,
      value.min,
      value.max,
      currentOperation,
      value.autoPricingResultType,
    ]
  );

  const handleRangeChange = useCallback(
    (v: { min?: number; max?: number }) => {
      onChange({
        currency: selectedCurrency,
        min: v.min,
        max: v.max,
        operation: currentOperation,
        autoPricingResultType: value.autoPricingResultType,
      });
    },
    [onChange, selectedCurrency, currentOperation, value.autoPricingResultType]
  );

  const handleSingleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = parseFloat(e.target.value);
      onChange({
        currency: selectedCurrency,
        [useMinValueOnly ? 'min' : 'max']: e.target.value ? v : undefined,
        [useMinValueOnly ? 'max' : 'min']: undefined,
        operation: currentOperation,
        autoPricingResultType: value.autoPricingResultType,
      });
    },
    [
      onChange,
      selectedCurrency,
      useMinValueOnly,
      currentOperation,
      value.autoPricingResultType,
    ]
  );

  const handleAutoPricingResultTypeChange = useCallback(
    (selectedType: string) => {
      onChange({
        currency: selectedCurrency,
        min: value.min,
        max: value.max,
        operation: currentOperation,
        autoPricingResultType: (selectedType || undefined) as
          | AutoPricingResultType
          | undefined,
      });
    },
    [onChange, selectedCurrency, value.min, value.max, currentOperation]
  );

  const handleClear = useCallback(() => {
    onChange({
      currency: selectedCurrency,
      min: undefined,
      max: undefined,
      operation: currentOperation,
      autoPricingResultType: undefined,
    });
    onClear();
  }, [onChange, onClear, selectedCurrency, currentOperation]);

  return (
    <ColumnFilter.Container isActive={isActive} columnId={columnId}>
      {/* Auto Pricing Result Type dropdown */}
      {hasAutoPricingOptions && (
        <>
          <div className={styles.label}>
            <Content id={ContentId.PriceState} />
          </div>
          <PosSelect
            value={value.autoPricingResultType || ''}
            onChange={handleAutoPricingResultTypeChange}
            valueOptionsContent={autoPricingResultTypeOptions}
            enableEmptySelection={true}
            style={{
              borderRadius: vars.borderRadius.base,
              fontSize: vars.typography.fontSize.base,
              padding: `${vars.spacing.sm} ${vars.spacing.m}`,
            }}
          />
        </>
      )}
      {/* Operation select dropdown */}
      {enableOperationSelect && (
        <>
          <div className={styles.label}>
            <Content id={ContentId.Operation} />
          </div>
          <PosSelect
            value={currentOperation ?? undefined}
            valueOptionsContent={NUMBER_RANGE_FILTER_OP_TO_CID}
            enableEmptySelection={false}
            onChange={handleOperationChange}
            style={{
              borderRadius: vars.borderRadius.base,
              fontSize: vars.typography.fontSize.base,
              padding: `${vars.spacing.sm} ${vars.spacing.m}`,
            }}
          />
        </>
      )}

      {/* Show currency selector only if multiple currencies exist */}
      {availableCurrencies.length > 1 && (
        <>
          <div className={styles.label}>
            <Content id={ContentId.Currency} />
          </div>
          <PosSelect
            value={selectedCurrency}
            onChange={handleCurrencyChange}
            valueOptionsContent={Object.fromEntries(
              availableCurrencies.map((c) => [
                c,
                `${c} (${getCurrencySymbol(c)})`,
              ])
            )}
            style={{
              borderRadius: vars.borderRadius.base,
              fontSize: vars.typography.fontSize.base,
              padding: `${vars.spacing.sm} ${vars.spacing.m}`,
            }}
            enableEmptySelection={false}
          />
        </>
      )}

      {/* Range inputs - show one or two inputs based on operation */}
      {needsTwoInputs ? (
        <ColumnFilter.RangeFilter
          min={value.min}
          max={value.max}
          onChange={handleRangeChange}
          minPlaceholder={minText}
          maxPlaceholder={maxText}
          prefix={<span>{currencySymbol}</span>}
        />
      ) : (
        <PosTextField
          type="number"
          value={(useMinValueOnly ? value.min : value.max) ?? ''}
          onChange={handleSingleInputChange}
          onClick={(e) => e.stopPropagation()}
          prefixDisplay={<span>{currencySymbol}</span>}
          rootProps={{
            style: {
              borderRadius: vars.borderRadius.base,
              fontSize: vars.typography.fontSize.base,
              padding: `${vars.spacing.sm} ${vars.spacing.m}`,
            },
          }}
        />
      )}

      <ColumnFilter.ClearButton
        text={clearFilterText}
        onClear={handleClear}
        disabled={!isActive}
        selectedCount={0}
      />
    </ColumnFilter.Container>
  );
};

/**
 * Extract unique values from table rows with counts for multi-select filters
 */
export const extractUniqueValues = (
  rows: Row<any>[],
  columnId: string,
  uniqueValueVariant?:
    | 'seatingSection'
    | 'seatTraits'
    | 'userTags'
    | 'marketplaceBroadcast'
    | 'outlierMode'
    | 'outlierStdDev'
    | 'compListingMode',
  venue?: any
): { options: Record<string, string>; counts: Record<string, number> } => {
  const options: Record<string, string> = {};
  const counts: Record<string, number> = {};
  // Special extractor for seating sections
  if (uniqueValueVariant === 'seatingSection') {
    rows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const value = row.getValue(columnId);
        if (value) {
          const { seating } = value as { seating: { section: string } };
          if (seating?.section) {
            const section = seating.section;
            if (!options[section]) {
              options[section] = section;
            }
            counts[section] = (counts[section] || 0) + 1;
          }
        }
      }
    });
    return { options, counts };
  }

  // Special extractor for seat traits (listing notes)
  if (uniqueValueVariant === 'seatTraits') {
    rows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const value = row.getValue(columnId);
        if (value) {
          const listingNotes = value as Array<{
            listingNoteId: number;
            listingNoteText: string;
          }> | null;
          if (listingNotes) {
            listingNotes.forEach((note) => {
              const noteKey = note.listingNoteId.toString();
              if (!options[noteKey]) {
                options[noteKey] = note.listingNoteText;
              }
              counts[noteKey] = (counts[noteKey] || 0) + 1;
            });
          }
        }
      }
    });
    return { options, counts };
  }

  // Special extractor for outlier mode - maps enum values to display names
  if (uniqueValueVariant === 'outlierMode') {
    rows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const listing = (row.original as any)?.listing;
        if (listing?.outMode) {
          const mode = listing.outMode;
          if (!options[mode]) {
            const displayName = (OUTLIER_MODE_TO_CID as any)[mode] || mode;
            options[mode] = displayName;
          }
          counts[mode] = (counts[mode] || 0) + 1;
        }
      }
    });
    return { options, counts };
  }

  // Special extractor for comp listing mode
  if (uniqueValueVariant === 'compListingMode') {
    rows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const listing = (row.original as any)?.listing;
        if (listing?.compMode) {
          const mode = listing.compMode;
          if (!options[mode]) {
            const displayName = (COMP_LISTING_MODE_TO_CID as any)[mode] || mode;
            options[mode] = displayName;
          }
          counts[mode] = (counts[mode] || 0) + 1;
        }
      }
    });
    return { options, counts };
  }

  // Special extractor for outlier standard deviations
  if (uniqueValueVariant === 'outlierStdDev') {
    rows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const listing = (row.original as any)?.listing;
        if (listing?.outlStdDev != null) {
          const stdDev = String(listing.outlStdDev);
          const displayName = STANDARD_DEVIATIONS_TO_CID[stdDev];
          if (displayName && !options[stdDev]) {
            options[stdDev] = displayName;
          }
          if (displayName) {
            counts[stdDev] = (counts[stdDev] || 0) + 1;
          }
        }
      }
    });
    return { options, counts };
  }

  // Special extractor for user tags - extracts user IDs from comma-separated or array values
  if (uniqueValueVariant === 'userTags') {
    rows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const value = row.getValue(columnId);
        if (value) {
          // Handle both string (comma-separated) and array formats
          const ids =
            typeof value === 'string'
              ? value.split(',').map((id) => id.trim())
              : Array.isArray(value)
                ? value
                : [value];
          ids.forEach((id) => {
            if (id && String(id).trim()) {
              const userId = String(id).trim();
              if (!options[userId]) {
                // Return user IDs as-is (display names will be resolved in UserTagFilterUI)
                options[userId] = userId;
              }
              counts[userId] = (counts[userId] || 0) + 1;
            }
          });
        }
      }
    });
    return { options, counts };
  }

  // Special extractor for marketplace broadcast status
  if (uniqueValueVariant === 'marketplaceBroadcast') {
    // Use passed venue to check if international event
    const isInternationalEvent = getIsInternationalEvent(venue?.ctryCode);
    rows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const listing = (row.original as any)?.listing;
        if (listing?.mkpListings) {
          listing.mkpListings
            .filter(
              (m: any) =>
                m.status === 'Listed' &&
                m.mkp &&
                !PosManagedMarketplace.includes(m.mkp) &&
                // Match cell rendering: only StubHub for international events
                (m.mkp === Marketplace.StubHub || !isInternationalEvent)
            )
            .forEach((m: any) => {
              const mkp = m.mkp;
              if (!options[mkp]) {
                options[mkp] = mkp;
              }
              counts[mkp] = (counts[mkp] || 0) + 1;
            });
        }
      }
    });
    return { options, counts };
  }

  // Default extractor
  rows.forEach((row) => {
    // Skip parent rows (only process leaf rows)
    if (row.getLeafRows().length === 0) {
      const value = row.getValue(columnId);
      if (value !== null && value !== 0) {
        const strValue = String(value);
        if (strValue.trim()) {
          if (!options[strValue]) {
            options[strValue] = strValue;
          }
          counts[strValue] = (counts[strValue] || 0) + 1;
        }
      }
    }
  });

  return { options, counts };
};
